import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface InboundMasterUnitFilters {
  status_id?: number | '';
  journey_type?: 'Forward' | 'Reverse' | 'Failed Delivery' | '';
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface City {
  id: number;
  name: string;
  code?: string;
}

export interface Branch {
  id: number;
  name: string;
  city_id?: number;
}

export interface User {
  id: number;
  name: string;
  type?: string;
}

export interface Status {
  id: number;
  name: string;
  badge?: string;
}

export interface Consignment {
  id: number;
  consignment_no: string;
  booking_type: string;
  service_type: string;
  created_at: string;
  chargeable_weight: number;
  status: Status;
  statusInMasterUnit?: string;
  reasonInMasterUnit?: string;
  attachmentInMasterUnit?: string;
  cod_amount?: number;
  total_price?: number;
}

export interface InboundMasterUnit {
  id: number;
  mu_number: string;
  seal_number: string;
  mu_type: string;
  type: string;
  journey_type: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  branch_id: number;
  client_id?: number;
  rider_id?: number;
  inbound_branch_id: number;
  origin: number;
  destination?: number;
  status_id: number;
  user?: User;
  branch?: Branch;
  inboundBranch?: Branch;
  client?: User;
  status?: Status;
  originCity?: City;
  destinationCity?: City;
  consignments?: Consignment[];
  handoverConsignments?: Consignment[];
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  per_page: number;
  total: number;
  last_page: number;
}

export interface MarkAsReceivedRequest {
  consignmentIds: number[];
}

export interface MarkAsShortReceivedRequest {
  consignment_id: number;
  reason: string;
  reason_attachment?: File;
}

export interface MarkAllAsShortReceivedRequest {
  consignment_ids: number[];
  reason: string;
}

export interface RemoveShortReceivedRequest {
  consignment_id: number;
}

export interface ShortReceivedPayload {
  consignment_id: number;
  masterunit_id: number;
  reason: string;
  reason_attachment?: File;
}

export interface BulkShortReceivedPayload {
  masterunit_id: number;
  consignmentIds: number[];
  reason: string;
}

// Inbound Master Unit API functions
export async function getInboundMasterUnits(filters: InboundMasterUnitFilters = {}) {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });

    const response = await apiClient.get(`/inbound-master-units?${params.toString()}`);
    const responseData = response.data;

    // Unwrap common { success, data } envelope if present
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;

    return payload as PaginatedResponse<InboundMasterUnit>;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to fetch inbound master units');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function getInboundMasterUnit(id: number) {
  try {
    const response = await apiClient.get(`/inbound-master-units/${id}`);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to fetch inbound master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function printInboundMasterUnit(id: number, awb: boolean = false) {
  const publicUrl = `${API_BASE_URL}/public/inbound-master-units/${id}/print?awb=${awb ? '1' : '0'}`;
  window.open(publicUrl, '_blank', 'noopener,noreferrer');
}

export async function markInboundMasterUnitAsReceived(id: number) {
  try {
    const response = await apiClient.post(`/inbound-master-units/${id}/receive`);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark master unit as received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function markAsReceived(id: number, data: MarkAsReceivedRequest) {
  try {
    const response = await apiClient.post(`/inbound-master-units/${id}/receive`, data);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark master unit as received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function markConsignmentAsShortReceived(data: ShortReceivedPayload) {
  try {
    const formData = new FormData();
    formData.append('consignment_id', data.consignment_id.toString());
    formData.append('reason', data.reason);
    if (data.reason_attachment) {
      formData.append('reason_attachment', data.reason_attachment);
    }

    const response = await apiClient.post(`/inbound-master-units/${data.masterunit_id}/short-received`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark consignment as short received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function markAsShortReceived(id: number, data: MarkAsShortReceivedRequest) {
  try {
    const formData = new FormData();
    formData.append('consignment_id', data.consignment_id.toString());
    formData.append('reason', data.reason);
    if (data.reason_attachment) {
      formData.append('reason_attachment', data.reason_attachment);
    }

    const response = await apiClient.post(`/inbound-master-units/${id}/short-received`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark consignment as short received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function markAllConsignmentsAsShortReceived(data: BulkShortReceivedPayload) {
  try {
    const response = await apiClient.post(`/inbound-master-units/${data.masterunit_id}/bulk-short-received`, {
      consignment_ids: data.consignmentIds,
      reason: data.reason
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark consignments as short received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function markAllAsShortReceived(id: number, data: MarkAllAsShortReceivedRequest) {
  try {
    const response = await apiClient.post(`/inbound-master-units/${id}/bulk-short-received`, data);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to mark consignments as short received');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function removeShortReceived(masterunitId: number, consignmentId: number) {
  try {
    const response = await apiClient.delete(`/inbound-master-units/${masterunitId}/remove-short-received`, {
      data: { consignment_id: consignmentId }
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to remove short received status');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function removeShortReceivedOld(id: number, data: RemoveShortReceivedRequest) {
  try {
    const response = await apiClient.delete(`/inbound-master-units/${id}/remove-short-received`, {
      data: data
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as InboundMasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to remove short received status');
    }
    throw new Error('An unknown error occurred');
  }
}
